import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { Product } from "../api.response";
import "./ProductDetails.css";
import ProductImageGallery from "./ProductSections/ProductImageGallery";
import ProductInfoGrid from "./ProductSections/ProductInfoGrid";
import ScreenTitle from "../components/ScreenTitle";  
import Loader from "../components/Loader";
import { useToast } from "../context/ToastContext";

const ProductDetails: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sizeQuantities, setSizeQuantities] = useState<Record<string, number>>({});
  const [generalQuantity, setGeneralQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) return;

      try {
        setLoading(true);
        const data = await api.getProductsDetail(productId);
        setProduct(data);
        setIsFavorite((data as any)?.isFavorite ?? false);
      } catch (err: any) {
        setError(err.message || "Failed to load product details");
        console.error("Error fetching product:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [productId]);

  const handleBack = () => {
    navigate(-1);
  };

const handleAddToCart = async () => {
  if (!product?.id) return;

  try {
    // Check if product has sizes enabled
    const hasSizes = product.sizeQuantities && product.sizeQuantities.length > 0;

    if (hasSizes) {
      // For products with sizes, collect all sizes with quantities > 0
      const sizesToAdd: string[] = [];
      const quantitiesToAdd: number[] = [];

      Object.entries(sizeQuantities).forEach(([size, qty]) => {
        if (qty > 0) {
          sizesToAdd.push(size);
          quantitiesToAdd.push(qty);
        }
      });

      if (sizesToAdd.length === 0) {
        alert("Please select quantity for at least one size");
        return;
      }

      // Add all sizes concurrently to the cart
      const addPromises = sizesToAdd.map((size, index) =>
        api.addToCart(product!.id.toString(), quantitiesToAdd[index], size)
          .catch((error) => {
            console.error(`Failed to add size ${size}:`, error);
            // Return error info for handling
            return { size, error, quantity: quantitiesToAdd[index] };
          })
      );

      const results = await Promise.allSettled(addPromises);

      // Handle results
      const successfulAdds: string[] = [];
      const failedAdds: string[] = [];

      results.forEach((result, index) => {
        const size = sizesToAdd[index];
        if (result.status === 'fulfilled') {
          if (!(result.value as any)?.error) {
            successfulAdds.push(size);
          } else {
            failedAdds.push(size);
          }
        } else {
          failedAdds.push(size);
        }
      });

      if (successfulAdds.length > 0) {
        alert(`${successfulAdds.length} item(s) successfully added to cart${failedAdds.length > 0 ? `. ${failedAdds.length} failed.` : ''}`);
      } else {
        alert("Failed to add any items to cart. Please try again.");
      }
    } else {
      // For products without sizes, use general quantity
      if (generalQuantity <= 0) {
        alert("Please select a quantity");
        return;
      }

      const response = await api.addToCart(product!.id.toString(), generalQuantity);

      if (response) {
        alert("Item successfully added to the cart");
      } else {
        throw new Error("Add to cart returned false");
      }
    }
  } catch (err: any) {
    console.error("Add to cart error:", err);

    // If adding fails (e.g., item already exists), try updating instead
    if (err.message?.includes('already in cart')) {
      try {
        const totalQuantity = product?.sizeQuantities && product.sizeQuantities.length > 0
          ? Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0)
          : generalQuantity;

        const updateResponse = await api.updateCart(product!.id.toString(), totalQuantity);

        if (updateResponse) {
          alert("Cart updated with new quantity");
        } else {
          alert("Failed to update cart. Please try again.");
        }
      } catch (updateErr) {
        console.error("Update cart error:", updateErr);
        alert("Failed to add/update item in cart. Please try again.");
      }
    } else {
      alert(`Failed to add item to cart: ${err.message || 'Unknown error'}`);
    }
  }
};

  const handleBuyNow = () => {
    if (!product) return;
    const totalQuantity = getTotalQuantity();
    
    // For products with sizes, pass the sizeQuantities; otherwise pass empty
    const hasSizes = product.sizeQuantities && product.sizeQuantities.length > 0;
    const sizeData = hasSizes ? sizeQuantities : {};
    
    navigate(`/checkout/${product.id}?quantity=${totalQuantity}`, { 
      state: { sizeQuantities: sizeData } 
    });
  };

  const handleToggleFavorite = async () => {
    if (!product) return;

    const next = !isFavorite;
    setIsFavorite(next);
    setProduct(prev =>
      prev
        ? {
            ...prev,
            favoriteCount: Math.max(
              0,
              (prev.favoriteCount ?? 0) + (next ? 1 : -1)
            ),
          }
        : prev
    );

    try {
      await api.addRemoveFavourite(product.id.toString());
      toast(next ? "Added to favourites" : "Removed from favourites", "success");
    } catch (e) {
      console.error("Favourite toggle failed", e);
      setIsFavorite(!next);
      setProduct(prev =>
        prev
          ? {
              ...prev,
              favoriteCount: Math.max(
                0,
                (prev.favoriteCount ?? 0) + (next ? -1 : 1)
              ),
            }
          : prev
      );
      toast("Something went wrong. Please try again.", "error");
    }
  };

  const getPriceForQuantity = (qty: number) => {
    if (!product?.priceTiers?.length) return 0;

    const applicableTier = product.priceTiers
      .sort((a, b) => a.minQty - b.minQty)
      .find(tier => qty >= tier.minQty && (!tier.maxQty || qty <= tier.maxQty));

    return applicableTier ? applicableTier.price : product.priceTiers[0].price;
  };

  const handleSizeQuantityChange = (size: string, qty: number) => {
    setSizeQuantities(prev => ({
      ...prev,
      [size]: Math.max(0, qty)
    }));
  };

  const handleGeneralQuantityChange = (qty: number) => {
    setGeneralQuantity(Math.max(1, qty));
  };

  const getTotalQuantity = () => {
    if (product?.sizeQuantities && product.sizeQuantities.length > 0) {
      return Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0);
    }
    return generalQuantity;
  };

  const getTotalPrice = () => {
    let grandTotal = 0;
    Object.entries(sizeQuantities).forEach(([_, qty]) => {
      if (qty > 0) {
        const price = getPriceForQuantity(qty);
        grandTotal += price * qty;
      }
    });
    return grandTotal;
  };

  if (loading) {
    return (
      <div className="product-details">
        <ScreenTitle getHeaderTitle = {()=>"Loading product..."} onBack={handleBack} />
        <div className="loading-container">
          <Loader size="large" type="spinner" text="Loading product details..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-details">
        <ScreenTitle getHeaderTitle = {()=>"Error"} onBack={handleBack} />
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-details">
        <ScreenTitle getHeaderTitle = {()=>"Product Not Found"} onBack={handleBack} />
        <div className="not-found-container">
          <p>Sorry, the product you're looking for doesn't exist.</p>
          <button className="back-button-text" onClick={handleBack}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-details">
      <ScreenTitle getHeaderTitle = {() => product.name} onBack={handleBack} />

      <div className="details-container">
        <div className="images-section">
          <ProductImageGallery images={product.images} altText={product.name} />
        </div>

        <div className="info-section">
          <ProductInfoGrid
            priceTiers={product.priceTiers}
            currentQuantity={getTotalQuantity()}
            brand={product.brand}
            tags={product.tags}
            currentPrice={product.priceTiers[0]?.price || 0}
            quantity={getTotalQuantity()}
            totalPrice={getTotalPrice()}
            availableQuantity={product.availableQuantity}
            favoriteCount={product.favoriteCount || 0}
            isFavorite={isFavorite}
            sizeQuantities={product.sizeQuantities}
            sizeQuantitiesState={sizeQuantities}
            generalQuantity={generalQuantity}
            onGeneralQuantityChange={handleGeneralQuantityChange}
            onSizeQuantityChange={handleSizeQuantityChange}
            getPriceForQuantity={getPriceForQuantity}
            onToggleFavorite={handleToggleFavorite}
            onAddToCart={handleAddToCart}
            onBuyNow={handleBuyNow}
          />
        </div>
      </div>

      <div className="description-card">
        <h2 className="section-title">Product Description</h2>
        <p className="product-description">{product.description}</p>
      </div>
    </div>
  );
};

export default ProductDetails;
